/**
 * Surgery Module Constants
 * Central source of truth for status codes, colors, labels, and configuration
 */

// ─── Surgery Status Codes ───────────────────────────────────────────────────
export const SURGERY_STATUS = {
  PENDING: 0,
  STARTED: 1,
  COMPLETED: 2,
  PRE_OP_COMPLETE: 3,
  WHO_CHECKLIST_STARTED: 4,
  POST_OP: 5,
  CANCELLED: 9,
};

export const SURGERY_STATUS_LABELS = {
  [SURGERY_STATUS.PENDING]: 'Pending',
  [SURGERY_STATUS.STARTED]: 'In Theatre',
  [SURGERY_STATUS.COMPLETED]: 'Completed',
  [SURGERY_STATUS.PRE_OP_COMPLETE]: 'Pre-Op Complete',
  [SURGERY_STATUS.WHO_CHECKLIST_STARTED]: 'WHO Checklist',
  [SURGERY_STATUS.POST_OP]: 'Post-Op',
  [SURGERY_STATUS.CANCELLED]: 'Cancelled',
};

export const SURGERY_STATUS_COLORS = {
  [SURGERY_STATUS.PENDING]: '#FF9800',      // Amber
  [SURGERY_STATUS.STARTED]: '#4CAF50',      // Green
  [SURGERY_STATUS.COMPLETED]: '#009688',    // Teal
  [SURGERY_STATUS.PRE_OP_COMPLETE]: '#2196F3', // Blue
  [SURGERY_STATUS.WHO_CHECKLIST_STARTED]: '#3F51B5', // Indigo
  [SURGERY_STATUS.POST_OP]: '#00BCD4',     // Cyan
  [SURGERY_STATUS.CANCELLED]: '#F44336',    // Red
};

// ─── Referral Status ────────────────────────────────────────────────────────
export const REFERRAL_STATUS = {
  PENDING: 'pending',
  SCHEDULED: 'scheduled',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
};

export const REFERRAL_STATUS_LABELS = {
  [REFERRAL_STATUS.PENDING]: 'Pending',
  [REFERRAL_STATUS.SCHEDULED]: 'Scheduled',
  [REFERRAL_STATUS.CANCELLED]: 'Cancelled',
  [REFERRAL_STATUS.EXPIRED]: 'Expired',
};

export const REFERRAL_STATUS_COLORS = {
  [REFERRAL_STATUS.PENDING]: '#FF9800',
  [REFERRAL_STATUS.SCHEDULED]: '#4CAF50',
  [REFERRAL_STATUS.CANCELLED]: '#F44336',
  [REFERRAL_STATUS.EXPIRED]: '#9E9E9E',
};

// ─── Urgency Levels ─────────────────────────────────────────────────────────
export const URGENCY = {
  ELECTIVE: 'elective',
  URGENT: 'urgent',
  EMERGENCY: 'emergency',
};

export const URGENCY_LABELS = {
  [URGENCY.ELECTIVE]: 'Elective',
  [URGENCY.URGENT]: 'Urgent',
  [URGENCY.EMERGENCY]: 'Emergency',
};

export const URGENCY_COLORS = {
  [URGENCY.ELECTIVE]: '#2196F3',   // Blue
  [URGENCY.URGENT]: '#FF9800',     // Amber
  [URGENCY.EMERGENCY]: '#F44336',  // Red
};

// ─── Patient / Pathway Types ────────────────────────────────────────────────
export const PATIENT_TYPE = {
  OUTPATIENT: 1,
  INPATIENT: 2,
};

export const PATHWAY = {
  OUTPATIENT: 'outpatient',
  INPATIENT: 'inpatient',
};

export const PATHWAY_LABELS = {
  [PATHWAY.OUTPATIENT]: 'Day-Case',
  [PATHWAY.INPATIENT]: 'Inpatient',
};

export const PATHWAY_COLORS = {
  [PATHWAY.OUTPATIENT]: '#00BCD4',  // Cyan
  [PATHWAY.INPATIENT]: '#673AB7',   // Deep Purple
};

// ─── Doctor Types ───────────────────────────────────────────────────────────
export const DOCTOR_TYPE = {
  INTERNAL: 'internal',
  VISITING_CONSULTANT: 'visiting_consultant',
  LOCUM: 'locum',
  REFERRED_SPECIALIST: 'referred_specialist',
};

export const DOCTOR_TYPE_LABELS = {
  [DOCTOR_TYPE.INTERNAL]: 'Internal',
  [DOCTOR_TYPE.VISITING_CONSULTANT]: 'Visiting',
  [DOCTOR_TYPE.LOCUM]: 'Locum',
  [DOCTOR_TYPE.REFERRED_SPECIALIST]: 'Referred',
};

export const DOCTOR_TYPE_COLORS = {
  [DOCTOR_TYPE.INTERNAL]: '#4CAF50',
  [DOCTOR_TYPE.VISITING_CONSULTANT]: '#FF9800',
  [DOCTOR_TYPE.LOCUM]: '#2196F3',
  [DOCTOR_TYPE.REFERRED_SPECIALIST]: '#9C27B0',
};

// ─── Operation Class ────────────────────────────────────────────────────────
export const OPERATION_CLASS = {
  MINOR: 'minor',
  INTERMEDIATE: 'intermediate',
  MAJOR: 'major',
  COMPLEX_MAJOR: 'complex_major',
};

export const OPERATION_CLASS_LABELS = {
  [OPERATION_CLASS.MINOR]: 'Minor',
  [OPERATION_CLASS.INTERMEDIATE]: 'Intermediate',
  [OPERATION_CLASS.MAJOR]: 'Major',
  [OPERATION_CLASS.COMPLEX_MAJOR]: 'Complex Major',
};

// ─── WHO Checklist Phases ───────────────────────────────────────────────────
export const WHO_PHASE = {
  SIGN_IN: 'sign_in',
  TIME_OUT: 'time_out',
  SIGN_OUT: 'sign_out',
};

export const WHO_PHASE_LABELS = {
  [WHO_PHASE.SIGN_IN]: 'Sign-In',
  [WHO_PHASE.TIME_OUT]: 'Time-Out',
  [WHO_PHASE.SIGN_OUT]: 'Sign-Out',
};

// ─── Anaesthesia Types ──────────────────────────────────────────────────────
export const ANAESTHESIA_TYPE = {
  GENERAL: 'general',
  REGIONAL: 'regional',
  LOCAL: 'local',
  SEDATION: 'sedation',
  SPINAL: 'spinal',
  EPIDURAL: 'epidural',
};

export const ANAESTHESIA_TYPE_LABELS = {
  [ANAESTHESIA_TYPE.GENERAL]: 'General',
  [ANAESTHESIA_TYPE.REGIONAL]: 'Regional',
  [ANAESTHESIA_TYPE.LOCAL]: 'Local',
  [ANAESTHESIA_TYPE.SEDATION]: 'Sedation',
  [ANAESTHESIA_TYPE.SPINAL]: 'Spinal',
  [ANAESTHESIA_TYPE.EPIDURAL]: 'Epidural',
};

// ─── Billing / Pay Status ───────────────────────────────────────────────────
export const PAY_STATUS = {
  UNPAID: 0,
  CASH_PAID: 1,
  DELETED: 2,
  INSURANCE_PAID: 3,
  REFUNDED: 5,
};

// ─── Outpatient Day-Case Steps ──────────────────────────────────────────────
export const DAY_CASE_STEP = {
  CHECK_IN: 'check_in',
  PROCEDURE: 'procedure',
  OBSERVATION: 'observation',
  DISCHARGE: 'discharge',
};

export const DAY_CASE_STEP_LABELS = {
  [DAY_CASE_STEP.CHECK_IN]: 'Check-In',
  [DAY_CASE_STEP.PROCEDURE]: 'Procedure',
  [DAY_CASE_STEP.OBSERVATION]: 'Observation',
  [DAY_CASE_STEP.DISCHARGE]: 'Discharge',
};

// ─── Surgery Pipeline Steps (for SurgeryStatusPipeline component) ───────────
export const PIPELINE_STEPS = [
  { key: 'referral', label: 'Referral', statusMatch: [SURGERY_STATUS.PENDING] },
  { key: 'scheduled', label: 'Scheduled', statusMatch: [SURGERY_STATUS.PENDING] },
  { key: 'pre_op', label: 'Pre-Op', statusMatch: [SURGERY_STATUS.PRE_OP_COMPLETE] },
  { key: 'who_checklist', label: 'WHO Check', statusMatch: [SURGERY_STATUS.WHO_CHECKLIST_STARTED] },
  { key: 'in_theatre', label: 'In Theatre', statusMatch: [SURGERY_STATUS.STARTED] },
  { key: 'post_op', label: 'Post-Op', statusMatch: [SURGERY_STATUS.POST_OP] },
  { key: 'completed', label: 'Completed', statusMatch: [SURGERY_STATUS.COMPLETED] },
];

// ─── Aging Warning Thresholds (for referral queue) ──────────────────────────
export const AGING_THRESHOLDS = {
  [URGENCY.ELECTIVE]: { warningDays: 14, color: '#FF9800' },
  [URGENCY.URGENT]: { warningDays: 3, color: '#FF5722' },
  [URGENCY.EMERGENCY]: { warningHours: 2, color: '#F44336' },
};

// ─── Visit Types ────────────────────────────────────────────────────────────
export const VISIT_TYPE = {
  NEW: 1,
  REVIEW: 2,
  ANTENATAL: 3,
  FIRST_TIME: 4,
  SURGERY: 5,
};

// ─── Complication Severity ──────────────────────────────────────────────────
export const COMPLICATION_SEVERITY = {
  MINOR: 'minor',
  MODERATE: 'moderate',
  MAJOR: 'major',
  LIFE_THREATENING: 'life_threatening',
  DEATH: 'death',
};

// ─── Aldrete Score Threshold ────────────────────────────────────────────────
export const ALDRETE_DISCHARGE_THRESHOLD = 9;

// ─── Fee Models ─────────────────────────────────────────────────────────────
export const FEE_MODEL = {
  PERCENTAGE_SPLIT: 'percentage_split',
  FLAT_RATE: 'flat_rate',
  DIRECT_BILLING: 'direct_billing',
};

// ─── Billing Status ────────────────────────────────────────────────────────
export const BILLING_STATUS = {
  PRE_AUTH_PENDING: 'pre_auth_pending',
  PRE_AUTH_APPROVED: 'pre_auth_approved',
  PRE_AUTH_DENIED: 'pre_auth_denied',
  CO_PAY_PENDING: 'co_pay_pending',
  CO_PAY_COLLECTED: 'co_pay_collected',
  CASH_UNPAID: 'cash_unpaid',
  CASH_PAID: 'cash_paid',
  ESTIMATE_DEFERRED: 'estimate_deferred',
};

export const BILLING_STATUS_LABELS = {
  [BILLING_STATUS.PRE_AUTH_PENDING]: 'Pre-auth pending',
  [BILLING_STATUS.PRE_AUTH_APPROVED]: 'Pre-auth approved',
  [BILLING_STATUS.PRE_AUTH_DENIED]: 'Pre-auth denied',
  [BILLING_STATUS.CO_PAY_PENDING]: 'Co-pay due',
  [BILLING_STATUS.CO_PAY_COLLECTED]: 'Co-pay collected',
  [BILLING_STATUS.CASH_UNPAID]: 'Cash — unpaid',
  [BILLING_STATUS.CASH_PAID]: 'Cash — paid',
  [BILLING_STATUS.ESTIMATE_DEFERRED]: 'Estimate — pending admission',
};

export const BILLING_STATUS_COLORS = {
  [BILLING_STATUS.PRE_AUTH_PENDING]: '#FF9800',   // Amber
  [BILLING_STATUS.PRE_AUTH_APPROVED]: '#4CAF50',  // Green
  [BILLING_STATUS.PRE_AUTH_DENIED]: '#F44336',    // Red
  [BILLING_STATUS.CO_PAY_PENDING]: '#FF9800',     // Amber
  [BILLING_STATUS.CO_PAY_COLLECTED]: '#4CAF50',   // Green
  [BILLING_STATUS.CASH_UNPAID]: '#9E9E9E',       // Gray
  [BILLING_STATUS.CASH_PAID]: '#4CAF50',  // Green
  [BILLING_STATUS.ESTIMATE_DEFERRED]: '#1565C0', // Blue
};

// Background colors for billing status badges (lighter tints)
export const BILLING_STATUS_BG_COLORS = {
  [BILLING_STATUS.PRE_AUTH_PENDING]: '#FFF3E0',
  [BILLING_STATUS.PRE_AUTH_APPROVED]: '#E8F5E9',
  [BILLING_STATUS.PRE_AUTH_DENIED]: '#FFEBEE',
  [BILLING_STATUS.CO_PAY_PENDING]: '#FFF3E0',
  [BILLING_STATUS.CO_PAY_COLLECTED]: '#E8F5E9',
  [BILLING_STATUS.CASH_UNPAID]: '#F5F5F5',
  [BILLING_STATUS.CASH_PAID]: '#E8F5E9',
  [BILLING_STATUS.ESTIMATE_DEFERRED]: '#E3F2FD',
};

// Dot indicator color for the sidebar navigation item
export const BILLING_STATUS_DOT_COLORS = {
  [BILLING_STATUS.PRE_AUTH_PENDING]: '#FF9800',
  [BILLING_STATUS.PRE_AUTH_APPROVED]: '#4CAF50',
  [BILLING_STATUS.PRE_AUTH_DENIED]: '#F44336',
  [BILLING_STATUS.CO_PAY_PENDING]: '#FF9800',
  [BILLING_STATUS.CO_PAY_COLLECTED]: '#4CAF50',
  [BILLING_STATUS.CASH_UNPAID]: '#9E9E9E',
  [BILLING_STATUS.CASH_PAID]: '#4CAF50',
  [BILLING_STATUS.ESTIMATE_DEFERRED]: '#1565C0',
};

/**
 * Whether the billing status is "cleared" — meaning the clinical flow can proceed.
 * estimate_deferred is cleared because inpatient billing is handled on admission.
 */
export const isBillingCleared = (billingStatus) => {
  return [
    BILLING_STATUS.PRE_AUTH_PENDING,
    BILLING_STATUS.PRE_AUTH_APPROVED,
    BILLING_STATUS.PRE_AUTH_DENIED,
    BILLING_STATUS.CO_PAY_COLLECTED,
    BILLING_STATUS.CASH_PAID,
    BILLING_STATUS.ESTIMATE_DEFERRED,
  ].includes(billingStatus);
};

/**
 * Map billing status to the primary action button label shown in the surgery list.
 */
export const BILLING_STATUS_PRIMARY_ACTION = {
  [BILLING_STATUS.PRE_AUTH_PENDING]: null,
  [BILLING_STATUS.PRE_AUTH_APPROVED]: null,
  [BILLING_STATUS.PRE_AUTH_DENIED]: null,
  [BILLING_STATUS.CO_PAY_PENDING]: 'Collect co-pay',
  [BILLING_STATUS.CO_PAY_COLLECTED]: null,
  [BILLING_STATUS.CASH_UNPAID]: null,
  [BILLING_STATUS.CASH_PAID]: null,
  [BILLING_STATUS.ESTIMATE_DEFERRED]: null,
};

// ─── Billing Activity Types (for timeline icons) ───────────────────────────
export const BILLING_ACTIVITY_ICONS = {
  surgery_scheduled: 'EventNote',
  preauth_submitted: 'Send',
  preauth_approved: 'CheckCircle',
  preauth_partially_approved: 'Warning',
  preauth_denied: 'Cancel',
  copay_collected: 'Payment',
  cash_paid: 'Payment',
  estimate_updated: 'Edit',
  estimate_posted: 'Receipt',
  status_update: 'Update',
};
